import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { type RootState } from "app";

export type CursorVisual = "crosshair" | "pointSnap" | "surfaceSnap" | "noHit" | "placed" | "transparentCircle";
export type DpadStyle = "normal" | "transparent";

/** Offset from D-pad center to the pick target (upper-left). */
export const PICK_TARGET_OFFSET = { x: -36, y: -36 };

interface MobileMeasureState {
    cursorActive: boolean;
    cursorPosition: { x: number; y: number } | null;
    /** Screen position of the pick-result indicator (projects to surface). */
    indicatorPosition: { x: number; y: number } | null;
    /** Pre-computed CSS rotate3d() transform for surface-aligned cursors. */
    indicatorTransform: string | null;
    cursorVisual: CursorVisual;
    dpadStyle: DpadStyle;
    isDragging: boolean;
    selectedPointIndex: number | null;
    selectedLineIndex: number | null;
    /** Number of dimension measure sets that existed before the current session started. */
    dimensionSessionStart: number;
    /** Currently selected laser ID (for showing overlay buttons). */
    selectedLaserId: string | null;
    /** Laser IDs created in the current session (for exit cleanup). */
    laserSessionIds: string[];
    /** Currently selected coordinate ID (for showing overlay buttons). */
    selectedCoordinateId: string | null;
    /** Coordinate IDs created in the current session (for exit cleanup). */
    coordinateSessionIds: string[];
    /** Point-to-point mode: "arrow" = default, "transparent" = drawing, "selected" = finalized measurement selected, "moving" = dragging point. */
    pointLineMode: "arrow" | "transparent" | "moving" | "selected";
    /** When set, DPAD drag moves this existing point instead of placing new ones. */
    movingPoint: { lineIndex: number; pointIndex: number } | null;
    /** True while the user is actively adding points (first point placed in arrow→transparent). False after finalization or selection. */
    activelyDrawing: boolean;
    /** Point line IDs created in the current session (for exit cleanup). */
    pointLineSessionIds: string[];
}

const initialState: MobileMeasureState = {
    cursorActive: false,
    cursorPosition: null,
    indicatorPosition: null,
    indicatorTransform: null,
    cursorVisual: "crosshair",
    dpadStyle: "normal",
    isDragging: false,
    selectedPointIndex: null,
    selectedLineIndex: null,
    dimensionSessionStart: 0,
    selectedLaserId: null,
    laserSessionIds: [],
    selectedCoordinateId: null,
    coordinateSessionIds: [],
    pointLineMode: "arrow",
    movingPoint: null,
    activelyDrawing: false,
    pointLineSessionIds: [],
};

export const mobileMeasureSlice = createSlice({
    name: "mobileMeasure",
    initialState,
    reducers: {
        setCursorActive(state, action: PayloadAction<boolean>) {
            state.cursorActive = action.payload;
            if (!action.payload) {
                state.isDragging = false;
            }
        },
        setCursorPosition(state, action: PayloadAction<{ x: number; y: number } | null>) {
            state.cursorPosition = action.payload;
        },
        setIndicatorPosition(state, action: PayloadAction<{ x: number; y: number } | null>) {
            state.indicatorPosition = action.payload;
        },
        setIndicatorTransform(state, action: PayloadAction<string | null>) {
            state.indicatorTransform = action.payload;
        },
        setCursorVisual(state, action: PayloadAction<CursorVisual>) {
            state.cursorVisual = action.payload;
        },
        setDpadStyle(state, action: PayloadAction<DpadStyle>) {
            state.dpadStyle = action.payload;
        },
        setIsDragging(state, action: PayloadAction<boolean>) {
            state.isDragging = action.payload;
        },
        setSelectedPoint(state, action: PayloadAction<{ lineIndex: number; pointIndex: number } | null>) {
            if (action.payload) {
                state.selectedLineIndex = action.payload.lineIndex;
                state.selectedPointIndex = action.payload.pointIndex;
            } else {
                state.selectedLineIndex = null;
                state.selectedPointIndex = null;
            }
        },
        setDimensionSessionStart(state, action: PayloadAction<number>) {
            state.dimensionSessionStart = action.payload;
        },
        setSelectedLaserId(state, action: PayloadAction<string | null>) {
            state.selectedLaserId = action.payload;
        },
        addLaserSessionId(state, action: PayloadAction<string>) {
            state.laserSessionIds.push(action.payload);
        },
        removeLaserSessionId(state, action: PayloadAction<string>) {
            state.laserSessionIds = state.laserSessionIds.filter((id) => id !== action.payload);
        },
        setSelectedCoordinateId(state, action: PayloadAction<string | null>) {
            state.selectedCoordinateId = action.payload;
        },
        addCoordinateSessionId(state, action: PayloadAction<string>) {
            state.coordinateSessionIds.push(action.payload);
        },
        removeCoordinateSessionId(state, action: PayloadAction<string>) {
            state.coordinateSessionIds = state.coordinateSessionIds.filter((id) => id !== action.payload);
        },
        setPointLineMode(state, action: PayloadAction<"arrow" | "transparent" | "moving" | "selected">) {
            state.pointLineMode = action.payload;
        },
        setMovingPoint(state, action: PayloadAction<{ lineIndex: number; pointIndex: number }>) {
            state.movingPoint = action.payload;
            state.pointLineMode = "moving";
        },
        clearMovingPoint(state) {
            state.movingPoint = null;
        },
        setActivelyDrawing(state, action: PayloadAction<boolean>) {
            state.activelyDrawing = action.payload;
        },
        addPointLineSessionId(state, action: PayloadAction<string>) {
            state.pointLineSessionIds.push(action.payload);
        },
        removePointLineSessionId(state, action: PayloadAction<string>) {
            state.pointLineSessionIds = state.pointLineSessionIds.filter((id) => id !== action.payload);
        },
        reset() {
            return initialState;
        },
    },
});

export const mobileMeasureActions = mobileMeasureSlice.actions;
export const mobileMeasureReducer = mobileMeasureSlice.reducer;

const selectMobileMeasureState = (state: RootState) => state.mobileMeasure;

export const selectCursorActive = createSelector(selectMobileMeasureState, (s) => s.cursorActive);
export const selectCursorPosition = createSelector(selectMobileMeasureState, (s) => s.cursorPosition);
export const selectIndicatorPosition = createSelector(selectMobileMeasureState, (s) => s.indicatorPosition);
export const selectIndicatorTransform = createSelector(selectMobileMeasureState, (s) => s.indicatorTransform);
export const selectCursorVisual = createSelector(selectMobileMeasureState, (s) => s.cursorVisual);
export const selectDpadStyle = createSelector(selectMobileMeasureState, (s) => s.dpadStyle);
export const selectIsDragging = createSelector(selectMobileMeasureState, (s) => s.isDragging);
export const selectSelectedPoint = createSelector(selectMobileMeasureState, (s) =>
    s.selectedLineIndex !== null && s.selectedPointIndex !== null
        ? { lineIndex: s.selectedLineIndex, pointIndex: s.selectedPointIndex }
        : null,
);
export const selectDimensionSessionStart = createSelector(selectMobileMeasureState, (s) => s.dimensionSessionStart);
export const selectSelectedLaserId = createSelector(selectMobileMeasureState, (s) => s.selectedLaserId);
export const selectLaserSessionIds = createSelector(selectMobileMeasureState, (s) => s.laserSessionIds);
export const selectSelectedCoordinateId = createSelector(selectMobileMeasureState, (s) => s.selectedCoordinateId);
export const selectCoordinateSessionIds = createSelector(selectMobileMeasureState, (s) => s.coordinateSessionIds);
export const selectPointLineMode = createSelector(selectMobileMeasureState, (s) => s.pointLineMode);
export const selectMovingPoint = createSelector(selectMobileMeasureState, (s) => s.movingPoint);
export const selectActivelyDrawing = createSelector(selectMobileMeasureState, (s) => s.activelyDrawing);
export const selectPointLineSessionIds = createSelector(selectMobileMeasureState, (s) => s.pointLineSessionIds);

/** Module-level ref for synchronous drag initiation from outside useMobileMeasureCursor (e.g. SVG long-press). */
export const mobileMeasureCursorRef: {
    forceStartDrag: ((pos: { x: number; y: number }) => void) | null;
    /** Set synchronously in handleMobileLongPress so onDragUpdate/onPlace can read it
     *  before the React render updates movingPointRef. */
    movingPoint: { lineIndex: number; pointIndex: number } | null;
    /** Snapshot of the point before the move started, so undo can restore the original position. */
    movingPointOriginal: {
        position: readonly [number, number, number];
        snap: unknown;
        objectId?: number;
        id: string;
    } | null;
    /** Set by coordinate HTML long-press so onPlace can detect move-mode before React re-renders. */
    movingCoordinateId: string | null;
} = { forceStartDrag: null, movingPoint: null, movingPointOriginal: null, movingCoordinateId: null };
